import React, { useEffect, useState } from 'react';
import copy from 'copy-to-clipboard';
import styled from 'styled-components';

import {
  CopyIcon,
  FlexRow,
  StyledTableRow,
  SuccessIcon,
  SvgIcon,
  useMatchMedia,
} from '@make-software/cspr-design';
import { BodyText, Link } from '@block-explorer/ui';

const StyledContainer = styled.span<{ isCopied: boolean }>`
  ${({ theme, isCopied }) => ({
    alignItems: 'center',
    cursor: 'pointer',
    display: 'inline-flex',
    height: 24,
    justifyContent: 'center',
    position: 'relative',
    pointerEvents: isCopied ? 'none' : 'initial',
    '>:not(:first-child)': {
      marginLeft: 8,
    },
    svg: {
      path: {
        fill: isCopied
          ? theme.styleguideColors.contentGreen
          : theme.styleguideColors.contentTertiary,
      },
      color: isCopied
        ? theme.styleguideColors.contentGreen
        : theme.styleguideColors.contentTertiary,
      ':hover': {
        color: theme.styleguideColors.fillPrimaryRed,
      },
      ':active': {
        color: theme.styleguideColors.fillPrimaryRedClick,
      },
      ...(isCopied && {
        animation: 'fadeInOut 3s',
      }),
    },
  })}

  ${StyledTableRow} & {
    visibility: hidden;
  }
  ${StyledTableRow}:hover & {
    visibility: visible;
  }
`;

const StyledBodyText = styled(BodyText)(({ theme }) => ({
  position: 'absolute',
  left: 12,
  animation: 'fadeInOut 3s',
  color: theme.styleguideColors.contentGreen,
  svg: {
    color: theme.styleguideColors.contentGreen,
  },
}));

/* eslint-disable-next-line */
export interface CopyButtonProps {
  value?: string | null;
  type?: 'full' | 'tiny';
  message?: string;
  label?: string;
}

// TODO: use from cspr-design
export function CopyButton({
  message = 'Copied!',
  type = 'tiny',
  value,
  label,
}: CopyButtonProps) {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  useEffect(() => {
    let timer;
    if (isCopied) {
      timer = setTimeout(() => {
        setIsCopied(false);
      }, 2900);
    }
    return () => clearTimeout(timer);
  }, [isCopied]);

  const copiedMessage = useMatchMedia([null, null, null, message], []);

  const handleClick = (ev) => {
    ev.stopPropagation();

    if (value) {
      copy(value);
      setIsCopied(true);
    }
  };

  if (value == null) {
    return null;
  }

  return (
    <FlexRow
      align={'center'}
      justify={'flex-start'}
      itemsSpacing={8}
      tag={'span'}
    >
      {label && (
        <Link color={'primaryBlue'} onClick={handleClick}>
          <BodyText size={2}>{label}</BodyText>
        </Link>
      )}
      <StyledContainer
        onClick={handleClick}
        isCopied={isCopied}
        tabIndex={0}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') {
            handleClick(ev);
          }
        }}
      >
        {!isCopied ? (
          <SvgIcon src={CopyIcon} size={16} role={'img'} alt={'Copy button'} />
        ) : (
          <>
            <SvgIcon
              src={SuccessIcon}
              size={16}
              role={'img'}
              alt={'Copied data success tick'}
            />
            {type === 'full' && (
              <StyledBodyText size={2}>{copiedMessage}</StyledBodyText>
            )}
          </>
        )}
      </StyledContainer>
    </FlexRow>
  );
}

export default CopyButton;
